import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  SESSION_ENDED_MESSAGE,
  takeSessionEndedNote,
} from 'src/api/consoleSessionEnd'
import EntryLayout, {
  CONSOLE_STANDFIRST,
} from 'src/components/layout/EntryLayout'
import Page from 'src/components/layout/Page'

import { LoginForm } from './parts'
import type { NewAccountQuestion } from './parts/NewAccountStep'

// ----------------------------------------------------------------------
// Sign in.
//
// The Tesla round trip leaves the console and comes back, so a failure on
// the way back has nowhere to live: the page that asked for it has already
// unmounted. The callback hands the reason over in router state and the form
// states it inline, which is why the error is read off the location here.
// ----------------------------------------------------------------------

interface LoginLocationState {
  error?: string
  /**
   * A Tesla sign-in that owes the account's authenticator code before its
   * Tesla account is linked: the ticket the code is answered with.
   */
  secondFactorTicket?: string
  /** The same, from a Google sign-in. */
  googleSecondFactorTicket?: string
  /** The same, from an Apple sign-in. */
  appleSecondFactorTicket?: string
  /** A Tesla, Google or Apple sign-in that no console account uses, to be asked. */
  newAccount?: NewAccountQuestion
}

const LoginPage = () => {
  const { pathname, state } = useLocation()
  const navigate = useNavigate()

  // Read once, then taken out of the history entry. The router keeps its
  // state in `window.history`, which survives a reload and comes back on
  // Back, so left in place the message reappears on a reload where nothing
  // failed, and again weeks later if someone walks back through history.
  const [handoverError] = useState(
    () => (state as LoginLocationState | null)?.error,
  )
  const [handoverTicket] = useState(
    () => (state as LoginLocationState | null)?.secondFactorTicket,
  )
  const [googleHandoverTicket] = useState(
    () => (state as LoginLocationState | null)?.googleSecondFactorTicket,
  )
  const [appleHandoverTicket] = useState(
    () => (state as LoginLocationState | null)?.appleSecondFactorTicket,
  )
  const [newAccount] = useState(
    () => (state as LoginLocationState | null)?.newAccount,
  )

  // Taken once, the same way: the session the backend refused is the reason
  // this page is showing, and it should say so on arrival and never again.
  const [sessionEnded] = useState(takeSessionEndedNote)

  useEffect(() => {
    const handedOver = state as LoginLocationState | null
    if (
      handedOver?.error ||
      handedOver?.secondFactorTicket ||
      handedOver?.googleSecondFactorTicket ||
      handedOver?.appleSecondFactorTicket ||
      handedOver?.newAccount
    ) {
      navigate(pathname, { replace: true, state: null })
    }
  }, [state, pathname, navigate])

  return (
    <Page title="Login">
      {/* Photographs rather than the illustration tier, and only here. This is
          the one screen where the fleet is the subject rather than the thing
          being operated, and a drawn car on the way into a console that
          manages real ones was the weakest claim the product makes. */}
      <EntryLayout
        headline="Run your fleet."
        standfirst={CONSOLE_STANDFIRST}
        photos
      >
        <LoginForm
          initialError={
            handoverError ?? (sessionEnded ? SESSION_ENDED_MESSAGE : undefined)
          }
          teslaSecondFactorTicket={handoverTicket}
          googleSecondFactorTicket={googleHandoverTicket}
          appleSecondFactorTicket={appleHandoverTicket}
          newAccount={newAccount}
        />
      </EntryLayout>
    </Page>
  )
}

export default LoginPage
